import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { debouncedSearch } from '@vsolv/utils/rxjs-operators';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import {
  MultiBadgeColumn,
  MultiTagColumn,
  PaginationConfig,
  SortableTableComponent,
  TableColumn,
  TextColumn,
  UserInfoColumn,
} from '@vsolv/vectors/table';
import { Customer } from '@wsphere/customers/domain';
import { CustomerService } from '@wsphere/customers/web';
import { SecurityService } from '@wsphere/staff/web';
import { Policy, Warranty } from '@wsphere/warranties/domain';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PolicyService } from '../../../policy/services';
import { WarrantyWebService } from '../../../warranty/services';

export type WarrantyListItem = Warranty.ListResponse['items'][number];

type DateRangeInput = Date | { startDate: Date | null; endDate: Date | null } | null;

// Extended response type to include nextCursor
interface WarrantyListResponseWithCursor extends Warranty.ListResponse {
  meta: Warranty.ListResponse['meta'] & { nextCursor?: string | null };
}

type WarrantyTableQueryParams = {
  customerName: string | null;
  endDate: Date | null;
  policyNumbers: string[];
  search: string;
  startDate: Date | null;
  status: Warranty.StatusFilter[];
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-warranty-table',
  templateUrl: './warranty-table.component.html',
  standalone: false,
})
export class WarrantyTableComponent extends SortableTableComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private currencyPipe: CurrencyPipe,
    private securitySvc: SecurityService,
    private warrantySvc: WarrantyWebService,
    private customerSvc: CustomerService,
    private policySvc: PolicyService
  ) {
    super();

    const initialQueryParams = this.getQueryParams();
    this.searchInputValue = initialQueryParams.search;
    this.form.patchValue(
      {
        search: initialQueryParams.search,
        status: initialQueryParams.status,
        startDate: initialQueryParams.startDate,
        endDate: initialQueryParams.endDate,
      },
      { emitEvent: false }
    );
  }

  protected readonly activated = Warranty.Status.ACTIVATED;
  readonly loading$ = new BehaviorSubject(true);
  readonly refresh$ = new BehaviorSubject(null);
  readonly pagination$ = new BehaviorSubject({ currentPage: 1, itemsPerPage: 10 });
  readonly hydrationComplete$ = new BehaviorSubject(false);
  warrantyCount: number | null = null;
  searchInputValue = '';

  positions = [
    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'end', overlayY: 'top' }),
    new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'end', overlayY: 'bottom' }),
    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' }),
  ];

  isFiltersOverlayOpen = false;

  private readonly destroy$ = new Subject<void>();
  private isDestroyed = false;
  private suppressQueryParamSync = true;

  // Cursor cache: stores cursor for each page number to enable navigation
  // Page 1 has no cursor (first request), page 2+ uses cursor from previous page's response
  private cursorCache = new Map<number, string>();
  private lastFiltersHash = '';

  override onSortChanged(sortData: { sortBy: string; sortOrder: 'ASC' | 'DESC' } | null) {
    super.onSortChanged(sortData);
    this.cursorCache.clear();
    this.pagination$.next({ ...this.pagination$.value, currentPage: 1 });
  }

  readonly canSellWarranty$: Observable<boolean> = this.securitySvc.globalDistributors$.pipe(
    switchMap(async globalDistributor => {
      const permissionKeys = globalDistributor?.map(dist => dist.permissionKey) ?? null;

      const [createAccess, createDraftAccess] = await Promise.all([
        this.securitySvc.hasAccess('wrt_CreateWarranty', permissionKeys),
        this.securitySvc.hasAccess('wrt_CreateDraftWarranty', permissionKeys),
      ]);

      return createAccess || createDraftAccess;
    })
  );

  protected form = this.formBuilder.group({
    selectedCustomer: [null as Customer.Model | null],
    status: [[] as Warranty.StatusFilter[]],
    search: ['' as string],
    policyIds: [[] as Policy.Model[]],
    startDate: [null as Date | null],
    endDate: [null as Date | null],
  });

  protected customerId$: Observable<string> = this.form.controls.selectedCustomer.valueChanges.pipe(
    startWith(this.form.controls.selectedCustomer.value),
    debounceTime(500),
    map(customer => customer?.id ?? '')
  );

  protected status$: Observable<Warranty.StatusFilter[] | null> = this.form.controls.status.valueChanges.pipe(
    startWith(this.form.controls.status.value),
    map(status => status ?? null)
  );

  protected policyIds$: Observable<string[] | null> = this.form.controls.policyIds.valueChanges.pipe(
    startWith(this.form.controls.policyIds.value),
    map(policyIds => policyIds?.map(policy => policy.id) ?? null)
  );

  protected search$: Observable<string> = this.form.controls.search.valueChanges.pipe(
    startWith(this.form.controls.search.value),
    debouncedSearch()
  );

  protected startDate$: Observable<Date | null> = this.form.controls.startDate.valueChanges.pipe(
    startWith(this.form.controls.startDate.value),
    map(date => {
      if (!date) return null;
      return date instanceof Date ? date : new Date(date);
    })
  );

  protected endDate$: Observable<Date | null> = this.form.controls.endDate.valueChanges.pipe(
    startWith(this.form.controls.endDate.value),
    map(date => {
      if (!date) return null;
      return date instanceof Date ? date : new Date(date);
    })
  );

  protected filters$ = combineLatest([
    this.customerId$,
    this.status$,
    this.search$,
    this.policyIds$,
    this.startDate$,
    this.endDate$,
  ]).pipe(
    map(([customerId, status, search, policyIds, startDate, endDate]) => {
      const filters = {
        ...(customerId ? { customerId } : {}),
        ...(status ? { status } : {}),
        ...(policyIds ? { policyIds } : {}),
        ...(search ? { search } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      };
      return filters;
    }),
    // When filters change, clear cursor cache and reset to page 1
    tap(filters => {
      const filtersHash = JSON.stringify(filters);
      if (filtersHash !== this.lastFiltersHash) {
        this.lastFiltersHash = filtersHash;
        this.cursorCache.clear();
        // Reset to page 1 when filters change
        if (this.pagination$.value.currentPage !== 1) {
          this.pagination$.next({ ...this.pagination$.value, currentPage: 1 });
        }
      }
    })
  );

  private paginatedData$ = combineLatest([
    this.pagination$.pipe(startWith(this.pagination$.value)),
    this.filters$.pipe(
      startWith({
        search: '',
        customerId: undefined,
        status: undefined,
        policyIds: undefined,
        startDate: undefined,
        endDate: undefined,
      })
    ),
    this.securitySvc.globalDistributors$.pipe(startWith([])),
    this.refresh$.pipe(startWith(null)),
    this.hydrationComplete$,
  ]).pipe(
    filter(([, , , , hydrationComplete]) => hydrationComplete),
    tap(() => this.loading$.next(true)),
    switchMap(([pagination, filters]) => {
      const sort = this.sort$.value;
      // Get cursor for the requested page (if we have it cached)

      // Cursor is only valid for the default created-DESC ordering — don't use it when a custom sort is active.
      const cursor = !sort && pagination.currentPage > 1 ? this.cursorCache.get(pagination.currentPage) : undefined;

      return this.warrantySvc
        .list$({
          // Only send page if we don't have a cursor (for backward compatibility)
          ...(cursor ? {} : { page: pagination.currentPage }),
          limit: pagination.itemsPerPage,
          search: filters?.search,
          customerId: filters?.customerId,
          status: filters?.status,
          policyIds: filters?.policyIds,
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          includeAllProperties: false,
          cursor,
          ...(sort && { sortBy: sort.sortBy, sortOrder: sort.sortOrder }),
        })
        .pipe(
          tap((data: WarrantyListResponseWithCursor) => {
            // Cache the nextCursor for the next page
            const nextPage = pagination.currentPage + 1;
            if (data.meta.nextCursor) {
              this.cursorCache.set(nextPage, data.meta.nextCursor);
            }
          }),
          finalize(() => this.loading$.next(false))
        );
    }),
    shareReplay(1),
    tap(data => {
      this.warrantyCount = data?.meta?.totalItems ?? 0;
    })
  );

  readonly warranties$: Observable<WarrantyListItem[]> = combineLatest([this.paginatedData$, this.filters$]).pipe(
    map(([listResponse]) => listResponse.items),
    startWith([])
  );

  readonly totalWarranties$: Observable<number | null> = combineLatest([this.paginatedData$, this.filters$]).pipe(
    map(([listResponse]) => listResponse.meta.totalItems || 0),
    startWith(null)
  );

  readonly paginationConfig$: Observable<Partial<PaginationConfig>> = combineLatest([
    this.paginatedData$,
    this.pagination$,
  ]).pipe(
    map(([data, pagination]) => ({
      ...data.meta,
      currentPage: pagination.currentPage,
    })),
    startWith(this.pagination$.value)
  );

  columns: TableColumn<unknown>[] = [
    new TextColumn<WarrantyListItem>({ header: 'Warranties', sortable: true }, warranty => ({
      text: warranty.plan?.title || '',
      description: warranty.contractNumber,
      classes: 'whitespace-nowrap',
    })),

    new UserInfoColumn<WarrantyListItem>({ header: 'Customer', fitContent: true, sortable: true }, warranty => ({
      name: warranty?.customer?.name,
      email: warranty?.customer?.email,
      photoUrl: warranty?.customer?.user?.photoURL,
      copyText: warranty?.customer?.name,
    })),

    new TextColumn<WarrantyListItem>({ header: 'Policy', sortable: true }, warranty => ({
      text: warranty.policy?.title || '',
      description: warranty.policy?.policyNumber,
      classes: 'max-w-[200px] truncate',
      tooltip: warranty.policy?.title || '',
    })),

    new MultiBadgeColumn<WarrantyListItem>({ header: 'Status', sortable: true }, warranty => {
      const label = Warranty.getStatusLabel(warranty?.status);
      const statusTheme: ThemeColor =
        warranty?.status === Warranty.Status.ACTIVATED
          ? 'success'
          : warranty?.status === Warranty.Status.REGISTERED
            ? 'default'
            : warranty?.status === Warranty.Status.EXPIRED
              ? 'danger'
              : warranty?.status === Warranty.Status.CANCELLED || warranty?.status === Warranty.Status.VOID
                ? 'warn'
                : 'default';

      const items = [
        {
          text: label ? label.charAt(0) + label.slice(1).toLowerCase() : '',
          displayStatusIcon: true,
          theme: statusTheme,
        },
        ...(warranty?.frozen
          ? [
              {
                text: warranty.externalPaymentHold ? 'Frozen · Awaiting seller receipts' : 'Frozen',
                theme: 'info' as const,
                iconStart: 'snowflake-01' as const,
              },
            ]
          : []),
      ];

      return { items };
    }),

    new TextColumn<WarrantyListItem>({ header: 'Sales' }, async warranty => {
      const total = this.currencyPipe.transform((warranty.total ?? 0) / 100) ?? '$0.00';
      return { text: total };
    }),

    new TextColumn<WarrantyListItem>({ header: 'Created', sortable: true }, warranty => ({
      text: moment(warranty.created).format('MMM DD, YYYY'),
      classes: 'whitespace-nowrap',
    })),

    new TextColumn<WarrantyListItem>({ header: 'Term start', sortable: true }, warranty => ({
      text: moment(warranty.termStart).format('MMM DD, YYYY'),
      classes: 'whitespace-nowrap',
    })),

    new TextColumn<WarrantyListItem>({ header: 'Expiry', sortable: true }, warranty => ({
      text: moment(warranty.expiryDate, ['YYYY-MM-DD', moment.ISO_8601], true).isValid()
        ? moment(warranty.expiryDate).format('MMM DD, YYYY')
        : moment(warranty.activationDeadline).format('MMM DD, YYYY'),
      classes: 'whitespace-nowrap',
    })),

    new MultiTagColumn<WarrantyListItem>({ header: 'Distributor', fitContent: true }, warranty => ({
      items: [{ text: warranty.distributor?.name || '-' }],
    })),
  ];

  async rowClicked(warranty: WarrantyListItem) {
    await this.router.navigate(['/warranty', warranty.id], { queryParamsHandling: 'preserve' });
  }

  async openNewWarranty() {
    await this.router.navigate(['provisioning']);
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.form.controls.selectedCustomer.value) count++;
    if (this.form.controls.policyIds.value?.length) count++;
    if (this.form.controls.status.value?.length) count++;
    if (this.form.controls.startDate.value || this.form.controls.endDate.value) count++;
    return count;
  }

  toggleFiltersOverlay() {
    this.isFiltersOverlayOpen = !this.isFiltersOverlayOpen;
  }

  closeFiltersOverlay() {
    this.isFiltersOverlayOpen = false;
  }

  clearAllFilters() {
    this.form.patchValue({
      selectedCustomer: null,
      policyIds: [],
      status: [],
      startDate: null,
      endDate: null,
    });
  }

  onDateRangeChange(dateRange: DateRangeInput) {
    // Handle the date range selection from the picker
    if (dateRange instanceof Date) {
      // Handle single date selection
      this.form.controls.startDate.setValue(dateRange);
      this.form.controls.endDate.setValue(dateRange);
    } else if (dateRange && typeof dateRange === 'object' && 'startDate' in dateRange) {
      // Allow partial date range selection (set each independently)
      this.form.controls.startDate.setValue(dateRange.startDate ?? null);
      this.form.controls.endDate.setValue(dateRange.endDate ?? null);
    } else if (dateRange === null) {
      // Clear the selection
      this.form.controls.startDate.setValue(null);
      this.form.controls.endDate.setValue(null);
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.hydrateFriendlyFiltersFromQueryParams();
    } catch (error) {
      // Keep default filters when hydration fails so the page can still initialize.
      console.error('Failed to hydrate warranty filters from query params:', error);
    }

    if (this.isDestroyed) return;

    this.form.patchValue(this.form.getRawValue(), { emitEvent: true });

    this.form.controls.search.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(search => {
      this.searchInputValue = search ?? '';
    });

    combineLatest([
      this.form.controls.selectedCustomer.valueChanges.pipe(startWith(this.form.controls.selectedCustomer.value)),
      this.form.controls.status.valueChanges.pipe(startWith(this.form.controls.status.value)),
      this.form.controls.search.valueChanges.pipe(startWith(this.form.controls.search.value), debouncedSearch()),
      this.form.controls.policyIds.valueChanges.pipe(startWith(this.form.controls.policyIds.value)),
      this.form.controls.startDate.valueChanges.pipe(startWith(this.form.controls.startDate.value)),
      this.form.controls.endDate.valueChanges.pipe(startWith(this.form.controls.endDate.value)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.suppressQueryParamSync) return;
        this.syncFiltersToQueryParams();
      });

    this.suppressQueryParamSync = false;
    this.syncFiltersToQueryParams();
    this.hydrationComplete$.next(true);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.hydrationComplete$.complete();
  }

  private getQueryParams(): WarrantyTableQueryParams {
    return {
      customerName: this.route.snapshot.queryParamMap.get('customer')?.trim() ?? null,
      endDate: this.parseDateParam(this.route.snapshot.queryParamMap.get('endDate')),
      policyNumbers: this.parseArrayParam('policies'),
      search: this.route.snapshot.queryParamMap.get('search')?.trim() ?? '',
      startDate: this.parseDateParam(this.route.snapshot.queryParamMap.get('startDate')),
      status: this.parseStatusParams(),
    };
  }

  private parseStatusParams(): Warranty.StatusFilter[] {
    const allowedStatuses = new Set<string>([...Object.values(Warranty.Status), Warranty.FROZEN_STATUS_FILTER]);

    return this.parseArrayParam('status').filter((status): status is Warranty.StatusFilter =>
      allowedStatuses.has(status)
    );
  }

  private parseArrayParam(key: string): string[] {
    const values = this.route.snapshot.queryParamMap.getAll(key);
    if (!values.length) {
      const value = this.route.snapshot.queryParamMap.get(key);
      return value
        ? value
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
        : [];
    }

    return values
      .flatMap(value => value.split(','))
      .map(item => item.trim())
      .filter(Boolean);
  }

  private parseDateParam(value: string | null): Date | null {
    if (!value) return null;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    const parsedDate = new Date(year, month - 1, day);

    if (
      Number.isNaN(parsedDate.getTime()) ||
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return null;
    }

    return parsedDate;
  }

  private formatDateParam(value: Date | null): string | null {
    if (!value) return null;

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private async hydrateFriendlyFiltersFromQueryParams(): Promise<void> {
    const params = this.getQueryParams();

    const [selectedCustomer, selectedPolicies] = await Promise.all([
      this.resolveCustomerFromQueryParam(params.customerName),
      this.resolvePoliciesFromQueryParams(params.policyNumbers),
    ]);

    if (this.isDestroyed) return;

    this.form.patchValue(
      {
        selectedCustomer,
        policyIds: selectedPolicies,
      },
      { emitEvent: false }
    );
  }

  private async resolveCustomerFromQueryParam(customerName: string | null): Promise<Customer.Model | null> {
    if (!customerName) return null;

    const response = await this.customerSvc.listCustomers({ page: 1, limit: 50, search: customerName });
    return (
      response.items.find(customer => customer.name.trim().toLowerCase() === customerName.trim().toLowerCase()) ?? null
    );
  }

  private async resolvePoliciesFromQueryParams(policyNumbers: string[]): Promise<Policy.Model[]> {
    const uniquePolicyNumbers = Array.from(
      new Set(policyNumbers.map(policyNumber => policyNumber.trim()).filter(Boolean))
    );
    if (!uniquePolicyNumbers.length) return [];

    const resolvedPolicies = await Promise.all(
      uniquePolicyNumbers.map(async policyNumber => {
        const response = await this.policySvc.list({ page: 1, limit: 50, search: policyNumber });
        return (
          response.items.find(
            policy => policy.policyNumber.trim().toLowerCase() === policyNumber.trim().toLowerCase()
          ) ?? null
        );
      })
    );

    return resolvedPolicies.filter((policy): policy is Policy.Model => !!policy);
  }

  private syncFiltersToQueryParams(): void {
    const queryParams = {
      customer: this.form.controls.selectedCustomer.value?.name || null,
      endDate: this.formatDateParam(this.form.controls.endDate.value),
      policies: this.form.controls.policyIds.value?.length
        ? this.form.controls.policyIds.value.map(policy => policy.policyNumber).join(',')
        : null,
      search: this.form.controls.search.value?.trim() || null,
      startDate: this.formatDateParam(this.form.controls.startDate.value),
      status: this.form.controls.status.value?.length ? this.form.controls.status.value.join(',') : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
